import { noop } from 'underscore';
import React, { useCallback, useMemo, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dropdown, Option, Input, mergeClasses } from '@fluentui/react-components';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';

import {
  getUrlPrefixAndDestination,
  getUpdatedFullUrlOnPrefixUpdate,
  getUpdatedFullUrlOnTextUpdate,
} from './utils';
import { validateAdUrl, UrlLengthLimit } from './validation';
import { useStyles } from './styles';

const urlPrefixOptions = [
  { key: 'https', text: 'https://', title: 'https://' },
  { key: 'http', text: 'http://', title: 'http://' },
];

export function UrlInputFluent({
  url,
  i18n,
  onChange,
  containerClassName,
  additionalValidation,
  prepopulateUrl,
  dropdownOptionAriaLabel,
  propertyKey,
  validateOnRender,
  additionalProperties,
  textFieldAriaLabel,
  customProtocolAriaLabel,
  customUrlProtocol,
  enableSpecialCharactersValidation,
  hideProtocol,
}) {
  const activity = useContext(ActivityContext);
  const customProtocolAriaLabelDefault = i18n.getString(_TL_('URL Custom Protocol input'));
  const dropdownOptionAriaLabelDefault = i18n.getString(_TL_('URL Protocol dropdown'));
  const textFieldAriaLabelDefault = i18n.getString(_TL_('URL Domain input'));

  const [urlPrefix, urlWithoutPrefix] = useMemo(
    () => getUrlPrefixAndDestination(url),
    [url]
  );

  const selectedOptionText = useMemo(() => {
    const selectedOption = urlPrefixOptions.filter(option => option.key === urlPrefix)[0] || urlPrefixOptions[0];
    return selectedOption.text;
  }, [urlPrefix]);

  const validateAndUpdateUrl = useCallback((currentURL) => {
    // first check URL validity. If valid then validate domain by need.
    let finalUrlValidatorError = validateAdUrl(currentURL, '', i18n, UrlLengthLimit, customUrlProtocol, enableSpecialCharactersValidation);
    if (!finalUrlValidatorError && additionalValidation) {
      finalUrlValidatorError = additionalValidation(currentURL);
    }

    onChange(finalUrlValidatorError ? [finalUrlValidatorError] : [], currentURL);
    if (activity) {
      activity.signal(`on${propertyKey}Change - ${JSON.stringify(currentURL)} - isValid: ${!finalUrlValidatorError}}`);
    }
  }, [activity, additionalValidation, customUrlProtocol, i18n, onChange, propertyKey]);

  const onUrlPrefixChanged = useCallback(
    (event, newItem) =>
      validateAndUpdateUrl(getUpdatedFullUrlOnPrefixUpdate(urlWithoutPrefix, newItem.optionValue)),
    [urlWithoutPrefix, validateAndUpdateUrl]
  );

  const onCustomUrlProtocolChanged = useCallback(
    (event, { value }) =>
      validateAndUpdateUrl(getUpdatedFullUrlOnPrefixUpdate(urlWithoutPrefix, value)),
    [urlWithoutPrefix, validateAndUpdateUrl]
  );

  const onTextChange = useCallback((event, { value }) => {
    const newValue = getUpdatedFullUrlOnTextUpdate(urlPrefix, value);

    if (newValue) {
      validateAndUpdateUrl(getUpdatedFullUrlOnTextUpdate(urlPrefix, value));
    }
  }, [validateAndUpdateUrl, urlPrefix]);

  useEffect(() => {
    if (!url && prepopulateUrl) {
      // Prepopulate final url with prepopulate on load if no value is provided.
      validateAndUpdateUrl(getUpdatedFullUrlOnTextUpdate(urlPrefix, prepopulateUrl));
    } else if (validateOnRender) {
      validateAndUpdateUrl(url);
    }
    // only validate on render and prepopulate on load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  const styles = useStyles();

  return (
    <div className={mergeClasses(styles.urlInputContainer, containerClassName)}>
      {
        !hideProtocol && customUrlProtocol &&
          <Input
            value={urlPrefix}
            aria-label={customProtocolAriaLabel || customProtocolAriaLabelDefault}
            onChange={onCustomUrlProtocolChanged}
            className={mergeClasses('url-protocol', styles.customUrlProtocol)}
            contentAfter="://"
          />
      }
      {
        !hideProtocol && !customUrlProtocol && (
          <Dropdown
            onOptionSelect={onUrlPrefixChanged}
            aria-label={dropdownOptionAriaLabel || dropdownOptionAriaLabelDefault}
            className={mergeClasses('url-protocol', styles.urlProtocol)}
            selectedOptions={[urlPrefix || urlPrefixOptions[0].key]}
            value={selectedOptionText}
          >
            {urlPrefixOptions.map(option => (
              <Option key={option.key} value={option.key} aria-label={i18n.getString(_TL_('{{optionText}} button'), { optionText: option.text })}>
                {option.text}
              </Option>
            ))}
          </Dropdown>
        )
      }
      <Input
        value={urlWithoutPrefix}
        onChange={onTextChange}
        className={mergeClasses('url-field', styles.urlInput)}
        aria-label={textFieldAriaLabel || textFieldAriaLabelDefault}
        {...additionalProperties}
      />
    </div>
  );
}

UrlInputFluent.propTypes = {
  containerClassName: PropTypes.string,
  url: PropTypes.string.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  additionalValidation: PropTypes.func,
  prepopulateUrl: PropTypes.string,
  propertyKey: PropTypes.string,
  validateOnRender: PropTypes.bool,
  additionalProperties: PropTypes.shape({}),
  dropdownOptionAriaLabel: PropTypes.string,
  textFieldAriaLabel: PropTypes.string,
  customProtocolAriaLabel: PropTypes.string,
  customUrlProtocol: PropTypes.bool,
  enableSpecialCharactersValidation: PropTypes.bool,
  hideProtocol: PropTypes.bool,
};

UrlInputFluent.defaultProps = {
  onChange: noop,
  onClick: noop,
  containerClassName: '',
  additionalValidation: noop,
  prepopulateUrl: '',
  customProtocolAriaLabel: null,
  dropdownOptionAriaLabel: null,
  textFieldAriaLabel: null,
  propertyKey: '',
  validateOnRender: false,
  additionalProperties: {},
  customUrlProtocol: false,
  enableSpecialCharactersValidation: false,
  hideProtocol: false,
};
